import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import toast from 'react-hot-toast';

/**
 * PlanGuard - Protects routes based on subscription plan and status.
 * 
 * IMPORTANT: This guard does NOT block 'inactive' users from general navigation.
 * Inactive users can browse the app in a limited trial mode.
 * This guard ONLY enforces:
 * 1. Premium feature access (Inventory, Finance) based on plan.
 * 2. Expired subscriptions (past_due, cancelled) are redirected.
 * 
 * @param {object} props
 * @param {React.ReactNode} props.children - The protected component
 * @param {boolean} props.requiresInventory - Route requires inventory access (premium)
 * @param {boolean} props.requiresFinance - Route requires finance access (premium)
 */
const PlanGuard = ({ children, requiresInventory = false, requiresFinance = false }) => {
  const location = useLocation();
  const { 
    canNavigateApp, 
    canAccessInventory, 
    canAccessFinance,
    subscriptionStatus 
  } = usePermissions();

  // Check if user can navigate the app at all
  // Only truly expired/cancelled users are blocked, NOT inactive (trial mode)
  if (!canNavigateApp) {
    // Show toast on first redirect (avoid spamming)
    if (location.pathname !== '/app/suscripcion') {
      toast.error('Tu suscripción ha expirado. Renueva para continuar.', { id: 'sub-expired' });
    }
    return <Navigate to="/app/suscripcion" state={{ from: location }} replace />;
  }

  // Check inventory access (premium feature)
  if (requiresInventory && !canAccessInventory) {
    toast.error('Mejora tu plan para acceder al módulo de Inventario', { id: 'plan-upgrade' });
    return <Navigate to="/app/dashboard" replace />;
  }

  // Check finance access (premium feature)
  if (requiresFinance && !canAccessFinance) {
    toast.error('Mejora tu plan para acceder al Dashboard de Rentabilidad', { id: 'plan-upgrade' });
    return <Navigate to="/app/dashboard" replace />;
  }

  return children;
};

export default PlanGuard;

